import React, { useState } from 'react';
import { Settings, Save, AlertTriangle, Trash2, Edit, RefreshCw, MapPin, Calendar, User, Info, X } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент для редактирования EXIF-данных
 */
const ExifEditingTools = ({ 
  exifData, 
  editedExifData, 
  setEditedExifData, 
  isEditing, 
  setIsEditing,
  onSave 
}) => {
  const [editSection, setEditSection] = useState(null); // 'camera', 'date', 'location', 'author'
  const [isRemoving, setIsRemoving] = useState(false);
  
  if (!exifData || !editedExifData) return null;

  // Функция для сохранения изменений в разделе
  const handleSaveSection = () => {
    setEditSection(null);
  };
  
  // Функция для отмены изменений в разделе
  const handleCancelEdit = () => {
    setEditSection(null);
    // Восстанавливаем оригинальные данные выбранного раздела
    setEditedExifData({
      ...editedExifData,
      [editSection]: { ...exifData[editSection] }
    });
  };
  
  // Функция для обновления полей в редактируемом разделе
  const handleFieldChange = (section, field, value) => {
    setEditedExifData({
      ...editedExifData,
      [section]: {
        ...editedExifData[section],
        [field]: value
      }
    });
  };
  
  // Функция удаления всех метаданных
  const handleRemoveAllMetadata = () => {
    // Создаем минимальный набор данных (только информация о файле и изображении)
    const minimalExifData = {
      ...editedExifData,
      camera: {
        make: '',
        model: '',
        software: '',
        lensModel: '',
        lensSerialNumber: ''
      },
      settings: {
        focalLength: '',
        focalLength35mm: '',
        aperture: '',
        shutterSpeed: '',
        iso: '',
        exposureCompensation: '',
        exposureProgram: '',
        whiteBalance: '',
        meteringMode: ''
      },
      date: {
        original: '',
        digitized: '',
        modified: new Date().toISOString()
      },
      location: {
        latitude: null,
        longitude: null,
        altitude: null,
        name: null,
        country: null,
        city: null
      },
      copyright: '',
      author: '',
      description: '',
      keywords: []
    };
    
    setEditedExifData(minimalExifData);
    setIsRemoving(false);
    alert("Метаданные были удалены из редактируемой копии изображения. Нажмите 'Сохранить изменения', чтобы применить удаление.");
  };
  
  // Функция удаления GPS-данных
  const handleRemoveLocationData = () => {
    setEditedExifData({
      ...editedExifData,
      location: {
        latitude: null,
        longitude: null,
        altitude: null,
        name: null,
        country: null,
        city: null
      }
    });
    
    alert("GPS-данные были удалены из редактируемой копии изображения. Нажмите 'Сохранить изменения', чтобы применить удаление.");
  };
  
  // Функция форматирования даты для полей ввода
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    return date.toISOString().substring(0, 16); // Формат YYYY-MM-DDTHH:MM для input type="datetime-local"
  };
  
  // Функция обработки изменения даты
  const handleDateChange = (field, value) => {
    const newDate = value ? new Date(value).toISOString() : '';
    handleFieldChange('date', field, newDate);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
          <Settings className="w-5 h-5 mr-2 text-purple-500" />
          Редактирование EXIF-данных
        </h2>
        
        {isEditing ? (
          <div className="flex space-x-2">
            <Button
              onClick={onSave}
              primary
              size="sm"
              icon={<Save className="w-4 h-4" />}
            >
              Сохранить изменения
            </Button>
            <Button
              onClick={() => setIsEditing(false)}
              variant="outline"
              size="sm"
              icon={<X className="w-4 h-4" />}
            >
              Отмена
            </Button>
          </div>
        ) : (
          <Button
            onClick={() => setIsEditing(true)}
            primary
            size="sm"
            icon={<Edit className="w-4 h-4" />}
          >
            Редактировать данные
          </Button>
        )}
      </div>

      {!isEditing && (
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-6 border border-blue-100 dark:border-blue-800">
          <div className="flex items-start">
            <Info className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                В этом разделе вы можете изменить или удалить метаданные изображения. 
                Изменения будут применены только к редактируемой копии и не затронут исходный файл.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Диалог удаления метаданных */}
      {isRemoving && (
        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 mb-6 border border-red-100 dark:border-red-800">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-red-800 dark:text-red-300 mb-2">
                Удаление метаданных
              </h3>
              <p className="text-sm text-red-700 dark:text-red-400 mb-4">
                Вы уверены, что хотите удалить все метаданные из изображения? Это действие нельзя отменить после сохранения.
              </p>
              <div className="flex space-x-3">
                <Button
                  onClick={handleRemoveAllMetadata}
                  variant="danger"
                  size="sm"
                >
                  Удалить все метаданные
                </Button>
                <Button
                  onClick={() => setIsRemoving(false)}
                  variant="outline"
                  size="sm"
                >
                  Отмена
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Секции редактирования */}
      <div className="space-y-6">
        {/* Информация о камере */}
        <EditSection
          title="Информация о камере и объективе"
          icon={<Camera className="w-4 h-4 text-purple-500" />}
          onEdit={() => setEditSection('camera')}
          isActive={editSection === 'camera'}
          isEditing={isEditing}
          onSave={handleSaveSection}
          onCancel={handleCancelEdit}
        >
          {editSection === 'camera' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Производитель камеры
                </label>
                <input
                  type="text"
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  value={editedExifData.camera.make || ''}
                  onChange={(e) => handleFieldChange('camera', 'make', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Модель камеры
                </label>
                <input
                  type="text"
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  value={editedExifData.camera.model || ''}
                  onChange={(e) => handleFieldChange('camera', 'model', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Объектив
                </label>
                <input
                  type="text"
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  value={editedExifData.camera.lensModel || ''}
                  onChange={(e) => handleFieldChange('camera', 'lensModel', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Программное обеспечение
                </label>
                <input
                  type="text"
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  value={editedExifData.camera.software || ''}
                  onChange={(e) => handleFieldChange('camera', 'software', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Серийный номер объектива
                </label>
                <input
                  type="text"
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  value={editedExifData.camera.lensSerialNumber || ''}
                  onChange={(e) => handleFieldChange('camera', 'lensSerialNumber', e.target.value)}
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DisplayItem label="Производитель" value={editedExifData.camera.make} />
              <DisplayItem label="Модель" value={editedExifData.camera.model} />
              <DisplayItem label="Объектив" value={editedExifData.camera.lensModel} />
              <DisplayItem label="Программное обеспечение" value={editedExifData.camera.software} />
              
              {editedExifData.camera.lensSerialNumber && (
                <DisplayItem label="Серийный номер объектива" value={editedExifData.camera.lensSerialNumber} />
              )}
            </div>
          )}
        </EditSection>

        {/* Настройки съемки */}
        <EditSection
          title="Настройки съемки"
          icon={<Settings className="w-4 h-4 text-purple-500" />}
          onEdit={() => setEditSection('settings')}
          isActive={editSection === 'settings'}
          isEditing={isEditing}
          onSave={handleSaveSection}
          onCancel={handleCancelEdit}
        >
          {editSection === 'settings' ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Диафрагма
                </label>
                <input
                  type="text"
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  value={editedExifData.settings.aperture || ''}
                  onChange={(e) => handleFieldChange('settings', 'aperture', e.target.value)}
                  placeholder="f/2.8"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Выдержка
                </label>
                <input
                  type="text"
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  value={editedExifData.settings.shutterSpeed || ''}
                  onChange={(e) => handleFieldChange('settings', 'shutterSpeed', e.target.value)}
                  placeholder="1/100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  ISO
                </label>
                <input
                  type="text"
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  value={editedExifData.settings.iso || ''}
                  onChange={(e) => handleFieldChange('settings', 'iso', e.target.value)}
                  placeholder="100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Фокусное расстояние (мм)
                </label>
                <input
                  type="text"
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  value={editedExifData.settings.focalLength || ''}
                  onChange={(e) => handleFieldChange('settings', 'focalLength', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Баланс белого
                </label>
                <select
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  value={editedExifData.settings.whiteBalance || ''}
                  onChange={(e) => handleFieldChange('settings', 'whiteBalance', e.target.value)}
                >
                  <option value="">Выберите...</option>
                  <option value="Авто">Авто</option>
                  <option value="Дневной свет">Дневной свет</option>
                  <option value="Облачно">Облачно</option>
                  <option value="Тень">Тень</option>
                  <option value="Вспышка">Вспышка</option>
                  <option value="Лампа накаливания">Лампа накаливания</option>
                  <option value="Флуоресцентный">Флуоресцентный</option>
                  <option value="Ручной">Ручной</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Режим экспозиции
                </label>
                <select
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  value={editedExifData.settings.exposureProgram || ''}
                  onChange={(e) => handleFieldChange('settings', 'exposureProgram', e.target.value)}
                >
                  <option value="">Выберите...</option>
                  <option value="Программный">Программный</option>
                  <option value="Приоритет диафрагмы">Приоритет диафрагмы</option>
                  <option value="Приоритет выдержки">Приоритет выдержки</option>
                  <option value="Ручной">Ручной</option>
                </select>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <DisplayItem label="Диафрагма" value={`f/${editedExifData.settings.aperture}`} />
              <DisplayItem label="Выдержка" value={editedExifData.settings.shutterSpeed} />
              <DisplayItem label="ISO" value={editedExifData.settings.iso} />
              <DisplayItem label="Фокусное расстояние" value={`${editedExifData.settings.focalLength} мм`} />
              <DisplayItem label="Баланс белого" value={editedExifData.settings.whiteBalance} />
              <DisplayItem label="Режим экспозиции" value={editedExifData.settings.exposureProgram} />
            </div>
          )}
        </EditSection>

        {/* Дата и время */}
        <EditSection
          title="Дата и время съемки"
          icon={<Calendar className="w-4 h-4 text-purple-500" />}
          onEdit={() => setEditSection('date')}
          isActive={editSection === 'date'}
          isEditing={isEditing}
          onSave={handleSaveSection}
          onCancel={handleCancelEdit}
        >
          {editSection === 'date' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Дата и время съемки
                </label>
                <input
                  type="datetime-local"
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  value={formatDateForInput(editedExifData.date.original)}
                  onChange={(e) => handleDateChange('original', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Дата и время оцифровки
                </label>
                <input
                  type="datetime-local"
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  value={formatDateForInput(editedExifData.date.digitized)}
                  onChange={(e) => handleDateChange('digitized', e.target.value)}
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DisplayItem 
                label="Дата и время съемки" 
                value={editedExifData.date.original 
                  ? new Date(editedExifData.date.original).toLocaleString('ru-RU')
                  : 'Не указано'
                } 
              />
              <DisplayItem 
                label="Дата и время оцифровки" 
                value={editedExifData.date.digitized 
                  ? new Date(editedExifData.date.digitized).toLocaleString('ru-RU')
                  : 'Не указано'
                } 
              />
            </div>
          )}
        </EditSection>

        {/* Информация о местоположении */}
        <EditSection
          title="Информация о местоположении"
          icon={<MapPin className="w-4 h-4 text-purple-500" />}
          onEdit={() => setEditSection('location')}
          isActive={editSection === 'location'}
          isEditing={isEditing}
          onSave={handleSaveSection}
          onCancel={handleCancelEdit}
          onCustomAction={handleRemoveLocationData}
          customActionLabel="Удалить данные GPS"
          customActionIcon={<Trash2 className="w-4 h-4" />}
          customActionVariant="danger"
        >
          {editSection === 'location' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Широта
                </label>
                <input
                  type="text"
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  value={editedExifData.location.latitude !== null ? editedExifData.location.latitude : ''}
                  onChange={(e) => handleFieldChange('location', 'latitude', e.target.value ? parseFloat(e.target.value) : null)}
                  placeholder="55.751244"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Долгота
                </label>
                <input
                  type="text"
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  value={editedExifData.location.longitude !== null ? editedExifData.location.longitude : ''}
                  onChange={(e) => handleFieldChange('location', 'longitude', e.target.value ? parseFloat(e.target.value) : null)}
                  placeholder="37.618423"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Город
                </label>
                <input
                  type="text"
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  value={editedExifData.location.city || ''}
                  onChange={(e) => handleFieldChange('location', 'city', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Страна
                </label>
                <input
                  type="text"
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  value={editedExifData.location.country || ''}
                  onChange={(e) => handleFieldChange('location', 'country', e.target.value)}
                />
              </div>
            </div>
          ) : (
            editedExifData.location.latitude !== null ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DisplayItem 
                  label="Широта" 
                  value={editedExifData.location.latitude !== null ? editedExifData.location.latitude.toFixed(6) + '°' : 'Нет данных'} 
                />
                <DisplayItem 
                  label="Долгота" 
                  value={editedExifData.location.longitude !== null ? editedExifData.location.longitude.toFixed(6) + '°' : 'Нет данных'} 
                />
                {editedExifData.location.city && (
                  <DisplayItem label="Город" value={editedExifData.location.city} />
                )}
                {editedExifData.location.country && (
                  <DisplayItem label="Страна" value={editedExifData.location.country} />
                )}
              </div>
            ) : (
              <div className="text-sm text-gray-600 dark:text-gray-400 py-2">
                Нет данных о местоположении
              </div>
            )
          )}
        </EditSection>

        {/* Авторская информация */}
        <EditSection
          title="Авторская информация"
          icon={<User className="w-4 h-4 text-purple-500" />}
          onEdit={() => setEditSection('author')}
          isActive={editSection === 'author'}
          isEditing={isEditing}
          onSave={handleSaveSection}
          onCancel={handleCancelEdit}
        >
          {editSection === 'author' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Автор
                </label>
                <input
                  type="text"
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  value={editedExifData.author || ''}
                  onChange={(e) => setEditedExifData({...editedExifData, author: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Авторские права
                </label>
                <input
                  type="text"
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  value={editedExifData.copyright || ''}
                  onChange={(e) => setEditedExifData({...editedExifData, copyright: e.target.value})}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Описание
                </label>
                <textarea
                  rows="3"
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  value={editedExifData.description || ''}
                  onChange={(e) => setEditedExifData({...editedExifData, description: e.target.value})}
                ></textarea>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DisplayItem label="Автор" value={editedExifData.author} />
              <DisplayItem label="Авторские права" value={editedExifData.copyright} />
              {editedExifData.description && (
                <div className="md:col-span-2">
                  <DisplayItem label="Описание" value={editedExifData.description} />
                </div>
              )}
            </div>
          )}
        </EditSection>
      </div>

      {/* Кнопки действий внизу */}
      <div className="mt-8 flex flex-wrap gap-3 justify-center">
        {isEditing && (
          <>
            <Button
              onClick={onSave}
              primary
              icon={<Save className="w-4 h-4" />}
            >
              Сохранить все изменения
            </Button>
            
            <Button
              onClick={() => setIsRemoving(true)}
              variant="danger"
              icon={<Trash2 className="w-4 h-4" />}
            >
              Удалить все метаданные
            </Button>
            
            <Button
              onClick={() => {
                setEditedExifData({...exifData});
                setIsEditing(false);
              }}
              variant="outline"
              icon={<RefreshCw className="w-4 h-4" />}
            >
              Сбросить изменения
            </Button>
          </>
        )}
      </div>

      <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/40 rounded-lg text-sm text-gray-600 dark:text-gray-400">
        <p>
          <strong>Примечание:</strong> В соответствии с российским законодательством о персональных данных, 
          рекомендуется удалять геометки и другие личные данные из фотографий перед их публикацией в открытом доступе.
        </p>
      </div>
    </div>
  );
};

// Компонент секции редактирования
const EditSection = ({ 
  title, 
  icon, 
  children, 
  onEdit, 
  isActive, 
  isEditing, 
  onSave, 
  onCancel,
  onCustomAction,
  customActionLabel,
  customActionIcon,
  customActionVariant
}) => {
  return (
    <div className={`rounded-lg border ${
      isActive 
        ? 'border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20' 
        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'}`
    }>
      <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-base font-medium text-gray-900 dark:text-white flex items-center">
          {icon && <span className="mr-2">{icon}</span>}
          {title}
        </h3>
        {isEditing && (
          <div className="flex space-x-2">
            {isActive ? (
              <>
                <Button
                  onClick={onSave}
                  primary
                  size="xs"
                  icon={<Save className="w-3 h-3" />}
                >
                  Сохранить
                </Button>
                <Button
                  onClick={onCancel}
                  variant="outline"
                  size="xs"
                  icon={<X className="w-3 h-3" />}
                >
                  Отмена
                </Button>
                {onCustomAction && customActionLabel && (
                  <Button
                    onClick={onCustomAction}
                    variant={customActionVariant || "outline"}
                    size="xs"
                    icon={customActionIcon || <Trash2 className="w-3 h-3" />}
                  >
                    {customActionLabel}
                  </Button>
                )}
              </>
            ) : (
              <Button
                onClick={onEdit}
                variant="outline"
                size="xs"
                icon={<Edit className="w-3 h-3" />}
              >
                Редактировать
              </Button>
            )}
          </div>
        )}
      </div>
      <div className="p-4">
        {children}
      </div>
    </div>
  );
};

// Компонент для отображения информационной пары ключ-значение
const DisplayItem = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-xs text-gray-500 dark:text-gray-400">
      {label}
    </span>
    <span className="text-sm font-medium text-gray-900 dark:text-white">
      {value || 'Нет данных'}
    </span>
  </div>
);

// Вспомогательный компонент для иконки камеры
const Camera = ({ className }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"></path>
    <circle cx="12" cy="13" r="3"></circle>
  </svg>
);

export default ExifEditingTools;